import { useState } from "react";
import { Link } from "react-router-dom";
import { ShieldAlert, RefreshCw, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AppRole } from "@/contexts/AuthContext";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface AccessDeniedProps {
  pathname: string;
  allowedRoles: AppRole[];
  userRoles: AppRole[];
}

export function AccessDenied({ pathname, allowedRoles, userRoles }: AccessDeniedProps) {
  const { toast } = useToast();
  const impersonation = useImpersonation();
  const [refreshing, setRefreshing] = useState(false);
  const [latestRoles, setLatestRoles] = useState<AppRole[] | null>(null);
  const [lastCheckedAt, setLastCheckedAt] = useState<Date | null>(null);

  const effectiveRoles = latestRoles ?? userRoles;
  const hasAccessNow =
    allowedRoles.length === 0 || effectiveRoles.some((r) => allowedRoles.includes(r));

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const { data: { user }, error: userErr } = await supabase.auth.getUser();
      if (userErr || !user) throw userErr ?? new Error("Not signed in");

      const { data, error } = await supabase.rpc("get_user_roles", { _user_id: user.id });
      if (error) throw error;

      const fresh = (data ?? []) as AppRole[];
      setLatestRoles(fresh);
      setLastCheckedAt(new Date());

      const granted = allowedRoles.length === 0 || fresh.some((r) => allowedRoles.includes(r));
      toast({
        title: granted ? "Access granted" : "Still no access",
        description: granted
          ? `Your roles now include access to ${pathname}. Reloading…`
          : `Current roles: ${fresh.length ? fresh.join(", ") : "none"}.`,
        variant: granted ? "default" : "destructive",
      });

      if (granted) {
        if (impersonation.active) {
          impersonation.stop();
        }
        setTimeout(() => window.location.assign(pathname), 250);
      }
    } catch (e: any) {
      toast({
        title: "Refresh failed",
        description: e?.message ?? "Could not re-check your roles.",
        variant: "destructive",
      });
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-lg border-destructive/30">
        <CardHeader className="space-y-2">
          <div className="flex items-center gap-2 text-destructive">
            <ShieldAlert className="h-6 w-6" />
            <CardTitle>Access denied</CardTitle>
          </div>
          <p className="text-sm text-muted-foreground">
            Your account doesn't have permission to view{" "}
            <code className="font-mono text-foreground">{pathname}</code>.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
              Allowed roles for this page
            </p>
            <div className="flex flex-wrap gap-1.5">
              {allowedRoles.length === 0 ? (
                <span className="text-sm text-muted-foreground">Any signed-in user</span>
              ) : (
                allowedRoles.map((r) => (
                  <Badge key={r} variant="secondary" className="font-mono text-xs">
                    {r}
                  </Badge>
                ))
              )}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                Your current roles {latestRoles ? "(latest)" : ""}
              </p>
              {lastCheckedAt && (
                <span className="text-[10px] text-muted-foreground">
                  Checked {lastCheckedAt.toLocaleTimeString()}
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {effectiveRoles.length === 0 ? (
                <Badge variant="destructive" className="font-mono text-xs">no roles assigned</Badge>
              ) : (
                effectiveRoles.map((r) => (
                  <Badge key={r} variant="outline" className="font-mono text-xs">
                    {r}
                  </Badge>
                ))
              )}
            </div>
            {latestRoles && hasAccessNow && (
              <p className="mt-2 flex items-center gap-1.5 text-xs text-primary">
                <CheckCircle2 className="h-3.5 w-3.5" />
                Access available — reloading the page…
              </p>
            )}
          </div>

          <div className="flex flex-wrap gap-2 pt-2">
            <Button onClick={handleRefresh} disabled={refreshing} variant="default">
              <RefreshCw className={refreshing ? "animate-spin" : ""} />
              <span>{refreshing ? "Checking…" : "Refresh my roles"}</span>
            </Button>
            <Button asChild variant="outline">
              <Link to="/dashboard">Go to dashboard</Link>
            </Button>
            <Button asChild variant="ghost">
              <Link to="/login">Sign in as different user</Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
